import { differenceInHours } from 'date-fns';
import type { Booking, Call, Position, RateCard, QuoteLineItem } from './types.js';
import { summarizeBudget, costForBookingByDay, ACTIVE_STATES } from './budget.js';
import type { Holiday } from './standard-timekeeping.js';
import type { SharedAllocation } from './sharedDay.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// A scheduled Call, or a worked interval standing in for one when costing
// actual timesheet time (actuals.ts). unpaidBreakMinutes is only ever set
// on the latter: it shortens the BILLED duration without moving start/end,
// so rest-gap checks keep using the real punches.
// roundToHalfHour: bill this span to the half hour (the standard rule) even
// for a role that isn't on standard timekeeping — set by actuals.ts for hourly
// work, where each clock-in/out period rounds to the half hour.
export type WorkSpan = Pick<Call, 'startTime' | 'endTime'> & { unpaidBreakMinutes?: number; roundToHalfHour?: boolean };

export function hoursForCall(call: WorkSpan): number {
  if (call.roundToHalfHour) return standardHoursForCall(call);
  if (!call.unpaidBreakMinutes) {
    return differenceInHours(call.endTime, call.startTime, { roundingMethod: 'ceil' });
  }
  const ms = call.endTime.getTime() - call.startTime.getTime() - call.unpaidBreakMinutes * 60000;
  return Math.ceil(Math.max(0, ms) / 3600000);
}

// Standard Timekeeping (Planning/Timekeeping.md) rounding — opt-in,
// parallel to hoursForCall above (which stays untouched for non-opted-in
// roles): round down to the nearest half hour unless more than 10 minutes
// past that mark, in which case round up to the next half hour. Elapsed ms
// is rounded to the nearest whole minute first so float/DST dust can't
// land exactly on the 10-minute boundary and misclassify it.
export function standardHoursForCall(call: WorkSpan): number {
  const rawMinutes = Math.max(
    0,
    Math.round((call.endTime.getTime() - call.startTime.getTime()) / 60000) - (call.unpaidBreakMinutes ?? 0),
  );
  const halfHourBlocks = Math.floor(rawMinutes / 30);
  const remainderMinutes = rawMinutes - halfHourBlocks * 30;
  const roundedBlocks = remainderMinutes > 10 ? halfHourBlocks + 1 : halfHourBlocks;
  return roundedBlocks / 2;
}

// Calendar-day bucket key — OT/day-rate billing resets per calendar day even
// though a Position or a Booking can span several. Takes an explicit IANA
// time zone (Organization.timezone, falling back to UTC) rather than either
// the ambient host's local time or a hardcoded UTC: this key must come out
// identically whether it's computed in the browser (the offer preview) or in
// a Cloud Function (the actual offer email) — those two environments don't
// share a local time zone, so deriving the calendar day from whichever
// happens to be running produced a different bucketing (and a different
// total) in each place. But raw UTC calendar days aren't right either for a
// real venue: an evening call in a US time zone starts well before UTC
// midnight and can still be running after it, so pure-UTC bucketing rolls a
// single evening show into the NEXT calendar day and merges it with a
// following day's calls, corrupting OT pooling for a real gig. Using the
// org's own explicit time zone in both places gives a bucketing that's both
// consistent AND matches the venue's actual calendar day.
export function dateKey(d: Date, timeZone = 'UTC'): string {
  // en-CA formats as YYYY-MM-DD, which is exactly the grouping key needed —
  // this also sidesteps getUTCMonth()'s 0-indexing footgun the old
  // getters-based version had (harmless as an opaque key, but needlessly
  // confusing to read).
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatTimeOfDay(d: Date): string {
  const h24 = d.getHours();
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 || 12;
  return `${h12}:${pad(d.getMinutes())} ${ampm}`;
}

function formatDateShort(d: Date): string {
  return `${WEEKDAYS[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
}

// Human-readable date/time span for one call, or several covered by a
// day-grouped labor line — used both on the quote line description and (as-is
// from there, no separate formatting) the invoice line's notes.
export function formatCallSpan(calls: Call[]): string {
  if (calls.length === 0) return '';
  const sorted = [...calls].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const date = formatDateShort(first.startTime);
  if (sorted.length === 1) {
    return `${date}, ${formatTimeOfDay(first.startTime)}–${formatTimeOfDay(first.endTime)}`;
  }
  return `${date}, ${first.type} ${formatTimeOfDay(first.startTime)}–${last.type} ${formatTimeOfDay(last.endTime)}`;
}

const UNSCHEDULED_DAY_KEY = '__unscheduled__';

// Derives one labor line per Position PER DAY it covers, budget-first: the
// position's total is max(Position.maxBudget, actualOfferedCost) * (1 +
// marginPct/100) — actualOfferedCost (from summarizeBudget) reflects what's
// really been offered to real bookings, so the quote never silently sits
// below what's actually been promised to crew, and marginPct is the
// client-facing cushion on top of whichever of the two numbers is higher.
// That total is then split across the position's days: proportional to each
// day's share of actualOfferedCost when there are active bookings to weigh
// it by, or spread evenly across the days when there's nothing booked yet
// (pure budget). The last day absorbs any rounding remainder so the days
// always sum exactly back to the position's total.
//
// `hours` is informational, not the billing driver — and for a day-rate
// line it's only carried at all when that day's scheduled hours actually
// push past the role's OT threshold (i.e. OT is really being charged that
// day); otherwise a day rate is a flat per-day charge and showing hours
// would misleadingly imply hourly billing. An hourly-rate line always
// carries its hours, same as before.
//
// Tier is always 0 here — picking a penalty tier for a specific line is a
// manual edit after generation (QuoteLineItem.tier exists for that; not
// surfaced in the builder UI yet).
export function buildLaborLines(
  positions: Position[],
  bookingsByPosition: Record<string, Booking[]>,
  calls: Call[],
  rateCards: RateCard[],
  marginPct = 0,
  timeZone = 'UTC',
  holidays?: Holiday[],
  shared?: SharedAllocation,
): QuoteLineItem[] {
  const callById = new Map(calls.map(c => [c.id, c]));
  const rateCardById = new Map(rateCards.map(rc => [rc.id, rc]));
  const summaries = summarizeBudget(positions, bookingsByPosition, calls, rateCards, timeZone, holidays, shared);
  const summaryByPositionId = new Map(summaries.map(s => [s.positionId, s]));

  const lines: QuoteLineItem[] = [];

  for (const pos of positions) {
    const summary = summaryByPositionId.get(pos.id);
    const actualOfferedCost = summary?.actualOfferedCost ?? 0;
    const base = Math.max(pos.maxBudget ?? 0, actualOfferedCost);
    const lineTotal = round2(base * (1 + marginPct / 100));
    const quantity = pos.quantity;
    const useDayRate = pos.rateType !== 'hourly';
    const roleLabel = pos.label ? `${pos.role} (${pos.label})` : pos.role;

    const card = pos.rateCardId ? rateCardById.get(pos.rateCardId) : undefined;
    const roleRate = card && pos.rateCardRole ? card.roles[pos.rateCardRole] : undefined;
    const otStartHrs = roleRate?.otStartHrs ?? 10;
    const minHourlyBill = roleRate?.minHourlyBill ?? 0;

    const linkedCalls = pos.callIds
      .map(id => callById.get(id))
      .filter((c): c is Call => Boolean(c))
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

    // Bucket the position's linked calls by calendar day — a position with
    // no calls at all (call-less/scopeDate, or fully unscheduled) is a
    // single bucket of its own.
    const dayKeys: string[] = [];
    const callsByDay = new Map<string, Call[]>();
    for (const call of linkedCalls) {
      const key = dateKey(call.startTime, timeZone);
      const list = callsByDay.get(key);
      if (list) list.push(call);
      else {
        callsByDay.set(key, [call]);
        dayKeys.push(key);
      }
    }
    if (dayKeys.length === 0) dayKeys.push(UNSCHEDULED_DAY_KEY);

    // Per-day actual cost, pooled across every active booking under this
    // position — mirrors costForBooking's own day bucketing, just broken
    // out by day instead of summed to one total, so it can weight how the
    // position's overall total is split across its day-lines below.
    const activeBookings = (bookingsByPosition[pos.id] ?? []).filter(b => ACTIVE_STATES.has(b.state));
    const dayActualCost = new Map<string, number>();
    for (const booking of activeBookings) {
      const byDay = costForBookingByDay(booking, pos, calls, rateCards, timeZone, holidays, 'estimate', shared);
      for (const [key, cost] of byDay) {
        dayActualCost.set(key, (dayActualCost.get(key) ?? 0) + cost.totalCost);
      }
    }

    let remaining = lineTotal;
    dayKeys.forEach((key, idx) => {
      const isLast = idx === dayKeys.length - 1;
      const dayCalls = callsByDay.get(key) ?? [];
      const share = actualOfferedCost > 0 ? (dayActualCost.get(key) ?? 0) / actualOfferedCost : 1 / dayKeys.length;
      const dayLineTotal = isLast ? round2(remaining) : round2(lineTotal * share);
      remaining -= dayLineTotal;
      const unitCost = quantity > 0 ? round2(dayLineTotal / quantity) : dayLineTotal;

      const dayHours = dayCalls.reduce((sum, c) => sum + hoursForCall(c), 0);
      const billedHours = Math.max(dayHours, minHourlyBill);
      const otCharged = useDayRate && billedHours > otStartHrs;
      const showHours = dayCalls.length > 0 && (!useDayRate || otCharged);

      // Day rate isn't billed by clock time even though hours still count
      // for display — the line should read as a day charge, not imply
      // hourly billing with a time range.
      const spanLabel = dayCalls.length
        ? useDayRate
          ? `Day Rate, ${formatDateShort(dayCalls[0].startTime)}`
          : formatCallSpan(dayCalls)
        : pos.scopeDate
          ? formatDateShort(pos.scopeDate)
          : 'Unscheduled';

      lines.push({
        id: `labor_${pos.id}_${key}`,
        source: 'labor' as const,
        description: `${roleLabel} — ${spanLabel}`,
        quantity,
        unitCost,
        lineTotal: dayLineTotal,
        callId: dayCalls[0]?.id,
        callIds: dayCalls.map(c => c.id),
        positionId: pos.id,
        rateCardId: pos.rateCardId,
        hours: showHours ? dayHours : undefined,
        tier: 0 as const,
      });
    });
  }

  return lines;
}

export interface QuoteTotals {
  subtotal: number;
  tax: number;
  total: number;
}

// taxRate is a percentage (e.g. 8.5 for 8.5%), matching Invoice Ninja's
// tax_rate1 convention.
export function quoteTotals(lineItems: QuoteLineItem[], taxRate?: number): QuoteTotals {
  const subtotal = round2(lineItems.reduce((sum, line) => sum + line.lineTotal, 0));
  const tax = taxRate ? round2(subtotal * (taxRate / 100)) : 0;
  const total = round2(subtotal + tax);
  return { subtotal, tax, total };
}

export interface InvoiceNinjaLineItem {
  product_key?: string;
  notes: string;
  cost: number;
  quantity: number;
}

// Reference copy — kept in sync with the (deliberately duplicated) version in
// functions/src/invoiceNinja.ts, which is the one actually invoked at invoice
// time (functions/ has no runtime dependency on @gigdriver/domain).
export function toInvoiceNinjaLineItems(quote: {
  lineItems: QuoteLineItem[];
}): InvoiceNinjaLineItem[] {
  return quote.lineItems.map(line => {
    if (line.source === 'labor' && line.hours) {
      return {
        product_key: line.productKey,
        notes: line.description,
        cost: round2(line.unitCost / line.hours),
        quantity: round2(line.hours * line.quantity),
      };
    }
    const notes =
      line.source === 'item' && line.itemDescription
        ? `${line.description}\n${line.itemDescription}`
        : line.description;
    return {
      product_key: line.productKey,
      notes,
      cost: line.unitCost,
      quantity: line.quantity,
    };
  });
}
